
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Info } from 'lucide-react';
import { BillingPeriod } from '@/types/product';
import { isValidRecurringBillingPeriod } from '@/utils/billingPeriodUtils';
import { billingOptions } from '@/lib/utils';

interface PriceInformationSectionProps {
  amount: string;
  setAmount: (value: string) => void;
  billing_period: BillingPeriod;
  setBillingPeriod: (value: BillingPeriod) => void;
  validationError: string | null;
}

const PriceInformationSection: React.FC<PriceInformationSectionProps> = ({
  amount,
  setAmount,
  billing_period,
  setBillingPeriod,
  validationError
}) => {
  const isSubscription = billing_period !== 'one_time';

  // Determine if we need to show a warning about recurring products
  const showRecurringWarning = isSubscription && !isValidRecurringBillingPeriod(billing_period);

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Price Information</h3>
      <div className="space-y-4">
        <div>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount ($)</Label>
            <Input 
              id="amount"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0.01"
              step="0.01"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing_period" className="flex items-center gap-1 mt-3">
              Billing Period 
              {isSubscription && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={billing_period}
              onValueChange={(val) => setBillingPeriod(val as BillingPeriod)}
            >
              <SelectTrigger 
                id="billing_period" 
                className={isSubscription && !isValidRecurringBillingPeriod(billing_period) ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {billingOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {showRecurringWarning && (
          <Alert variant="destructive" className="bg-amber-50 border-amber-200">
            <Info className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-amber-800">Billing Period Required</AlertTitle>
            <AlertDescription className="text-amber-700">
              You've selected a subscription product but need to specify a recurring billing period (not one-time).
            </AlertDescription>
          </Alert>
        )}
        
        {validationError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default PriceInformationSection;
